import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Typography, Select } from 'foundations-library/components'
import ListItems from './ListItems'
import { processLink } from '../helpers'
import { useWidgetContext } from 'providers'
import GoBack from '../../../../apps/apps-marketplace/src/components/GoBack'
import { useHistory, useLocation, useParams } from 'react-router-dom'

const SMS_REGISTRATION_SECTION_SLUG = 'sms-registration'
const SMS_REGISTRATION_NO_FILTER_VALUE = 'all'
const SMS_REGISTRATION_FILTER_OPTIONS = [
    // {
    //     text: 'No filter',
    //     value: SMS_REGISTRATION_NO_FILTER_VALUE
    // },
    {
        text: 'Brand',
        value: 'brand-enrollment'
    },
    {
        text: 'Campaign',
        value: 'campaign-enrollment'
    },
    {
        text: 'Number assignment',
        value: 'sms-number-enrollment'
    }
]

const useStyles = makeStyles(theme => ({
    root: {
        maxWidth: '1050px',
        margin: 'auto',
        marginTop: '40px'
    },
    filter: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '30px'
    },
    select: {
        marginLeft: '10px',
        width: '300px'
    }
}))

interface Option {
    text: string
    value: string
}

interface Props {
    sectionSlugProp?: string | null
    searchProp?: string | null
    isModal?: boolean
}

/**
 *
 */
const App = ({ sectionSlugProp = null, searchProp = null, isModal = false }: Props) => {
    const styles = useStyles()
    const [allArticles, setAllArticles] = useState([])
    const { sectionSlug: mainSlug } = useParams()
    const location = useLocation()
    const [selectedValue, setSelectedValue] = useState('')
    const [options, setOptions] = useState<Option[]>([])
    const history = useHistory()
    const sectionSlug = sectionSlugProp || mainSlug

    const { getArticlesBySectionSlug, remoteValue } = useWidgetContext()
    const isSmsRegistrationSection = [sectionSlug, sectionSlugProp].includes(SMS_REGISTRATION_SECTION_SLUG)

    const search = searchProp || location.search

    const getSmsRegistrationFilter = (search?: string) => {
        const filterValue = new URLSearchParams(search).get('filter') || search
        const hasValidFilter = SMS_REGISTRATION_FILTER_OPTIONS.some((option) => option.value === filterValue)

        return hasValidFilter ? filterValue : SMS_REGISTRATION_NO_FILTER_VALUE
    }

    const setupSmsSection = () => {
        setOptions(SMS_REGISTRATION_FILTER_OPTIONS)
        setSelectedValue(getSmsRegistrationFilter(search))
    }

    const setupRegularSection = () => {
        const formattedOptions = remoteValue.sections
            .filter(({ slug }) => slug !== SMS_REGISTRATION_SECTION_SLUG)
            .map(({ sectionName, slug }) => ({
                text: sectionName,
                value: slug
            }))
        const hasMatchingSection = formattedOptions.some(({ value }) => value === sectionSlug)
        setOptions(formattedOptions)
        setSelectedValue(hasMatchingSection ? sectionSlug : (formattedOptions[0]?.value || ''))
    }

    useEffect(() => {
        if (isSmsRegistrationSection) {
            setupSmsSection()
            return
        }
        setupRegularSection()
    }, [isSmsRegistrationSection, search, remoteValue.sections, sectionSlug])

    useEffect(() => {
        if (selectedValue) {
            getArticles(selectedValue)
        }
    }, [selectedValue, sectionSlug])

    const changeSection = (value: string) => {
        if (value) {
            if (isSmsRegistrationSection) {
                history.replace({
                    pathname: location.pathname,
                    search: `?filter=${value}`
                })
            }
            setSelectedValue(value)
        }
    }

    const redirect = (val: string) => {
        const targetSectionSlug = isSmsRegistrationSection ? SMS_REGISTRATION_SECTION_SLUG : selectedValue
        const [articleSlug, ...hashParts] = val.split('#')
        const articleHash = hashParts.length ? `#${hashParts.join('#')}` : ''
        const smsFilterSearch = isSmsRegistrationSection ? `?filter=${selectedValue || SMS_REGISTRATION_NO_FILTER_VALUE}` : ''
        processLink(`/support-center/${targetSectionSlug}/${articleSlug}${smsFilterSearch}${articleHash}`, history)
    }

    const getArticles = (val: string) => {
        if (isSmsRegistrationSection) {
            const section = getArticlesBySectionSlug(SMS_REGISTRATION_SECTION_SLUG)
            if (!section) return

            if (val === SMS_REGISTRATION_NO_FILTER_VALUE) {
                setAllArticles(section.articles)
                return
            }

            setAllArticles(section.articles.filter((article) => article.category === val))
            return
        }

        const newVal = getArticlesBySectionSlug(val)
        if (newVal) {
            setAllArticles(newVal.articles)
        }
    }

    const goBack = <GoBack title={'How to articles'} />
    const renderTitle = (title) => <Typography variant='h4' align='center' color='neutral-900'>{title}</Typography>
    return (
        <>
            {!isModal ? goBack : renderTitle('How to articles')}
            <div className={styles.root}>
                {selectedValue && (
                    <div className={styles.filter} data-testid="list">
                        <Typography variant='subtitle2'>Filter by type:</Typography>
                        <div className={styles.select}>
                            <Select options={options} onChange={changeSection} value={selectedValue} required={true} data-testid='filter-select' />
                        </div>
                    </div>
                )}
                {allArticles?.length > 0 && (
                    <ListItems items={allArticles} isModal={isModal} onRedirect={(articleSlug) => redirect(articleSlug)} />
                )}
            </div>
        </>
    )
}

export default App
